import { defineStore } from 'pinia'
import DataLayer from '@services/data-layer/index'
import localSave from '@utils/local-save'
import { useAuthStore } from '@state/stores/auth'

const TYPES = ['mixes', 'artists', 'shows', 'genres']
const LIST_KEY = {
    mixes: 'mixesList',
    artists: 'artistsList',
    shows: 'showsList',
    genres: 'genresList',
}

function currentMemberId() {
    return useAuthStore().currentUser?.members_id?.id
}

export const useFavoritesStore = defineStore('favorites', {
    state: () => ({
        mixesList: [],
        artistsList: [],
        showsList: [],
        genresList: [],
        channelIds: localSave.get('favoriteChannels') || localSave.get('favorites') || [],
        loaded: {},
    }),
    getters: {
        genreIds: state => state.genresList.map(item => item.id),
        likedMixes: state => state.mixesList.map(record => record.mixes).filter(Boolean),
        followedArtists: state => state.artistsList.map(record => record.artists).filter(Boolean),
        followedShows: state => state.showsList.map(record => record.shows).filter(Boolean),
        followedGenres: state => state.genresList.map(record => record.genres).filter(Boolean),
        isLoaded: state => type => Boolean(state.loaded[type]),
    },
    actions: {
        setList(type, data) {
            if (!data) return
            this[LIST_KEY[type]] = data
        },

        setFavoriteChannels(ids) {
            this.channelIds = ids
            localSave.save('favoriteChannels', ids)
        },

        setTypeLoaded(type) {
            this.loaded = { ...this.loaded, [type]: true }
        },

        // Load all favorite lists for the current user (on login / route change).
        init() {
            return Promise.all(TYPES.map(type => this.fetchType(type)))
        },

        async fetchType(type) {
            const membersId = currentMemberId()
            if (!membersId) return
            // `list` already flattens each record down to a top-level `id`.
            const { body } = await DataLayer.favorites.list({ type, membersId, limit: 100000 })
            this.setList(type, body || [])
            this.setTypeLoaded(type)
        },

        async fetchPage({ type, offset = 0, limit = 30 }) {
            const membersId = currentMemberId()
            if (!membersId) return []
            const { body } = await DataLayer.favorites.list({ type, membersId, offset, limit })
            return body || []
        },

        async add({ type, id }) {
            const membersId = currentMemberId()
            await DataLayer.favorites.add({ type, membersId, id })
            await this.fetchType(type)
        },

        async remove({ type, id }) {
            const membersId = currentMemberId()
            await DataLayer.favorites.remove({ type, membersId, id })
            this.setList(type, this[LIST_KEY[type]].filter(item => item.id !== id))
        },

        toggleChannel(channelId) {
            const channelIds = this.channelIds.includes(channelId)
                ? this.channelIds.filter(id => id !== channelId)
                : [...this.channelIds, channelId]
            this.setFavoriteChannels(channelIds)
        },
    },
})
